import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { accountApi, type AccountScreen } from '@/services/accountApi';

/**
 * Account "Screens" section: lists the user's registered devices, shows the
 * plan's device limit, and lets them remove a device to free a slot (the fix
 * for a SCREEN_LIMIT_REACHED block). Never screen-gated on the backend.
 */
export function ScreensSection(): JSX.Element {
  const { t } = useTranslation();
  const [screens, setScreens] = useState<AccountScreen[]>([]);
  const [limit, setLimit] = useState<number | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('loading');

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await accountApi.getScreens();
      setScreens(Array.isArray(res?.screens) ? res.screens : []);
      setLimit(typeof res?.limit === 'number' ? res.limit : null);
      setStatus('idle');
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const remove = useCallback(async (id: string) => {
    if (!window.confirm(t('account.screens.confirmRemove'))) return;
    await accountApi.removeScreen(id);
    await load();
  }, [load, t]);

  return (
    <section className="admin-card" id="screens">
      <h2 className="admin-card__title">{t('account.screens.title')}</h2>
      <p className="muted" data-testid="screens-usage">
        {limit == null
          ? t('account.screens.usageUnlimited', { used: screens.length })
          : t('account.screens.usage', { used: screens.length, limit })}
      </p>
      {screens.length === 0 && status === 'idle' && <p className="muted">{t('account.screens.empty')}</p>}
      <ul className="screens-list">
        {screens.map((s) => (
          <li key={s.id} className="screens-list__item">
            <div>
              <strong>{s.label}</strong>{' '}
              {s.current && <span className="badge" data-testid="screens-current-badge">{t('account.screens.thisDevice')}</span>}
              <div className="muted">{t('account.screens.lastSeen', { when: new Date(s.lastSeenAt).toLocaleString() })}</div>
            </div>
            <button type="button" className="btn-ghost" data-testid="screens-remove" onClick={() => void remove(s.id)}>
              {t('account.screens.remove')}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
